import { isForbiddenError, isSuccess } from '@/context/services/type';
import { useGetHeaderWithOfficeId } from '@/features/ip_address_whitelists/hooks/useGetHeaderWithOfficeId';
import { useQueryClient } from '@tanstack/react-query';
import {
  CommonError,
  getGetIpAddressWhitelistsQueryKey,
  useCreateIpAddressWhitelist,
  useDeleteIpAddressWhitelist,
  useGetIpAddressWhitelistsSuspense,
  useUpdateIpAddressWhitelist,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';

export const useGetIpAddressWhitelistsService = () => {
  const { data, error } = useGetIpAddressWhitelistsSuspense();
  if (error) throw error;
  return useMemo(() => ({ data: data?.data }), [data]);
};

export const useCreateIpAddressWhitelistService = (officeId: string) => {
  const queryClient = useQueryClient();
  const headerWithOfficeId = useGetHeaderWithOfficeId(officeId);
  const { mutateAsync: _mutateAsync } =
    useCreateIpAddressWhitelist(headerWithOfficeId);

  const mutateAsync = useCallback(
    async (ip_address: string) => {
      const resp = await _mutateAsync({ data: { ip_address } });
      if (isSuccess(resp.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetIpAddressWhitelistsQueryKey(),
        });
        return { status: 'success' as const, data: resp.data };
      } else if (isForbiddenError(resp.status)) {
        return {
          status: 'forbidden' as const,
          redirect_url: resp?.headers?.location ?? undefined,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
      return {
        status: 'error' as const,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        errors: (resp.data ?? {}) as CommonError,
      };
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync };
};

export const useUpdateIpAddressWhitelistService = (officeId: string) => {
  const queryClient = useQueryClient();
  const headerWithOfficeId = useGetHeaderWithOfficeId(officeId);
  const { mutateAsync: _mutateAsync } =
    useUpdateIpAddressWhitelist(headerWithOfficeId);

  const mutateAsync = useCallback(
    async (id: string, ip_address: string) => {
      const resp = await _mutateAsync({ id, data: { ip_address } });
      if (isSuccess(resp.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetIpAddressWhitelistsQueryKey(),
        });
        return { status: 'success' as const, data: resp.data };
      } else if (isForbiddenError(resp.status)) {
        return {
          status: 'forbidden' as const,
          redirect_url: resp?.headers?.location ?? undefined,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: resp.data as CommonError,
        };
      }
      return {
        status: 'error' as const,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        errors: (resp.data ?? {}) as CommonError,
      };
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync };
};

export const useDeleteIpAddressWhitelistService = (officeId: string) => {
  const queryClient = useQueryClient();
  const headerWithOfficeId = useGetHeaderWithOfficeId(officeId);
  const { mutateAsync: _mutateAsync } =
    useDeleteIpAddressWhitelist(headerWithOfficeId);

  const mutateAsync = useCallback(
    async (id: string) => {
      const resp = await _mutateAsync({ id });
      if (isSuccess(resp.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetIpAddressWhitelistsQueryKey(),
        });
        return { status: 'success' as const };
      } else if (isForbiddenError(resp.status)) {
        return {
          status: 'forbidden' as const,
          redirect_url: resp?.headers?.location ?? undefined,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: ((resp.data as unknown) ?? {}) as CommonError,
        };
      }
      return {
        status: 'error' as const,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        errors: ((resp.data as unknown) ?? {}) as CommonError,
      };
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync };
};
